import React, { useState } from 'react';
import classnames from 'classnames';
import GlobalProps from '../globalProps';
import { Icon } from '../Icon/Icon';
import { FilterTag } from '../../model/filter';
import { Shortcut } from '../Shortcut/Shortcut';
import { TagsGroup } from '../Tag/TagsGroup';
import './ListSearch.css';
import { IconName } from '../Icon/IconName';

interface Props extends GlobalProps {
  query: FilterTag[];
  loading?: boolean;
  onChange(query: FilterTag[]): void;
  iconName?: IconName;
  negation?: boolean;
  size?: 'normal' | 'large';
}

export function parsePendingTag(tag: string, negation: boolean): FilterTag {
  const regex = /^-/gi;
  return {
    label: negation ? tag.replace(regex, '') : tag,
    exclude: negation ? tag.startsWith('-') : false,
  };
}

export function ListSearch({
  className,
  style,
  query = [],
  loading,
  onChange,
  iconName = 'search--sm',
  negation = true,
  size = 'normal',
}: Props): JSX.Element {
  const [pendingTag, setPendingTag] = useState(' ');
  const [focus, setFocus] = useState(false);

  function addTag(): void {
    if (pendingTag.trim()) {
      const tagToAdd = parsePendingTag(pendingTag.trim(), negation);
      const tagIsUnique = query.every((q) => tagToAdd.label !== q.label);

      if (tagIsUnique) {
        const newTags = [...query];
        newTags.push(tagToAdd);
        onChange(newTags);
      }

      setPendingTag(' ');
    }
  }

  function removeLastTag(): void {
    if (!pendingTag) {
      const newTags = [...query];
      newTags.pop();
      onChange(newTags);
    }
  }

  function removeTagAtIndex(index: number): void {
    const newTags = [...query];
    newTags.splice(index, 1);
    onChange(newTags);
  }

  return (
    <div
      className={classnames('input', className, {
        'list-search': size === 'normal',
        'list-search--large': size === 'large',
        'list-search--loading': loading,
      })}
      style={style}
    >
      <Icon name={iconName} className="input__icon" />
      <div className="input__container">
        <div className="input__input-wrapper">
          <TagsGroup
            tags={query.map((item) => ({
              id: `${item.label}_${item.exclude}`,
              label: item.label,
              backgroundColor: item.exclude ? 'var(--color-tint)' : undefined,
            }))}
            onRemove={removeTagAtIndex}
            onClear={() => onChange([])}
            input={
              <input
                name="list-search"
                className="input__input"
                type="text"
                onChange={(e) => {
                  setPendingTag(e.currentTarget.value);
                }}
                value={pendingTag}
                onFocus={() => {
                  setFocus(true);
                }}
                onBlur={() => {
                  setFocus(false);
                }}
              />
            }
            expandHiddenTags="bottom-end"
          />
        </div>
        <div className="list-search__progress" />
      </div>
      <Shortcut keys="Enter" onInvoke={addTag} overrideInput={focus} />
      <Shortcut keys=" " onInvoke={addTag} overrideInput={focus} />
      <Shortcut keys="Backspace" onInvoke={removeLastTag} overrideInput={focus} />
    </div>
  );
}
